import { useState, useCallback, useMemo, useRef } from 'react';
import { TEAM_ACCEPT_RETURN_TO } from '../../smartLinks/smlAuth/teamAcceptReturnTo';
import { useTRPCClientAPI, useTRPCMutation } from '../../../trpc/trpc';
import { clearReferralCode, getReferralCode } from '../../music24/utils/referralStorage';
import { generateEventId, getFbp, getFbc, fbqTrack } from '../../music24/utils/facebookPixel';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import { hasTrackingConsent } from '../../analytics/consent/trackingConsent';
import { getSignupAttribution } from '../../analytics/attribution/signupAttribution';

// The Meta signals of a signup: the pixel cookies and the consent flag. music24-api sends the
// CompleteRegistration event to Meta only when `trackingConsent` is true (consent bar Accept).
function metaSignals(): { trackingConsent: boolean; fbp?: string; fbc?: string } {
  if (!hasTrackingConsent()) {
    return { trackingConsent: false };
  }
  return { trackingConsent: true, fbp: getFbp(), fbc: getFbc() };
}

export type PasswordlessAuthStep = 'email' | 'code';

// This hook is SML-only (its only consumers are smlAuth's useSocialSignIn and
// useAuthFormPasswordless), so the new-vs-returning-user segmentation lives here
// next to the existing isNewUser side effects (the CompleteRegistration pixel)
// instead of being duplicated in each consumer's onSuccess.
type SignInMethod = 'email' | 'apple' | 'google';

/** The mode of the sign-in page: `?mode=signin`, an invite (`returnTo=team/accept`), else signup. */
function authMode(): 'signin' | 'signup' | 'invite' {
  const params = new URLSearchParams(window.location.search);
  if (params.get('returnTo') === TEAM_ACCEPT_RETURN_TO) {
    return 'invite';
  }
  return params.get('mode') === 'signin' ? 'signin' : 'signup';
}

/** The kind of a failed code send, from the tRPC error of music24-api. */
function codeSendResult(err: unknown): 'rateLimited' | 'turnstile' | 'disposableEmail' | 'failed' {
  const data = (err as { data?: { code?: string; errorType?: string } } | null)?.data;
  if (data?.code === 'TOO_MANY_REQUESTS') {
    return 'rateLimited';
  }
  if (data?.errorType === 'M24_TURNSTILE_FAILED') {
    return 'turnstile';
  }
  if (data?.errorType === 'M24_DISPOSABLE_EMAIL') {
    return 'disposableEmail';
  }
  return 'failed';
}

function codeCheckResult(err: unknown): 'wrong' | 'rateLimited' | 'failed' {
  const code = (err as { data?: { code?: string } } | null)?.data?.code;
  if (code === 'TOO_MANY_REQUESTS') {
    return 'rateLimited';
  }
  return code === 'BAD_REQUEST' ? 'wrong' : 'failed';
}

function trackSmlAuthFinished(method: SignInMethod, isNewUser: boolean): void {
  SMLAnalytics.trackSignInCompleted(method, isNewUser).catch(console.error);
}

export interface UsePasswordlessAuthOptions {
  onSuccess?: (token: string, isNewUser: boolean) => void;
  onError?: (error: Error) => void;
  /**
   * The Turnstile token for one code send (`auth.requestCode`, `auth.resendCode`). The email-code
   * sign-in passes it; social sign-in needs none. `undefined` sends no token.
   */
  getTurnstileToken?: () => Promise<string | undefined>;
}

export type SocialSignInInput =
  | { provider: 'google'; authCode: string }
  // `code` is the Apple authorization code. `nonce` is the RAW nonce from auth.socialNonce.
  | { provider: 'apple'; idToken: string; code: string; nonce: string; name?: string };

export interface UsePasswordlessAuthReturn {
  // Current step
  step: PasswordlessAuthStep;

  // Email that was entered in step 1
  email: string;

  // Loading states
  isRequestingCode: boolean;
  isVerifyingCode: boolean;
  isResendingCode: boolean;
  isSocialSigningIn: boolean;

  // Error state
  error: Error | null;

  // Actions
  requestCode: (email: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  socialSignIn: (input: SocialSignInInput) => Promise<void>;
  goBackToEmail: () => void;
  /**
   * Opens the code step for an address with no new send: the code that went out before (a reload,
   * Back, "I already have a code"). The page keeps the address in `sessionStorage`.
   */
  showCodeStep: (email: string) => void;
  clearError: () => void;
}

export function usePasswordlessAuth(options: UsePasswordlessAuthOptions): UsePasswordlessAuthReturn {
  const { onSuccess, onError } = options;
  // Read at send time, so a new function from the caller needs no new mutation.
  const getTurnstileTokenRef = useRef(options.getTurnstileToken);
  getTurnstileTokenRef.current = options.getTurnstileToken;

  const [step, setStep] = useState<PasswordlessAuthStep>('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<Error | null>(null);

  const trpcClient = useTRPCClientAPI();

  // Stable event_id per hook instance — dedupes client pixel vs server CAPI.
  const eventId = useMemo(() => generateEventId(), []);

  // Request code mutation
  const requestCodeMutation = useTRPCMutation({
    mutationFn: async (emailValue: string) => {
      const turnstileToken = await getTurnstileTokenRef.current?.();
      return trpcClient.auth.requestCode.mutate({ email: emailValue, turnstileToken });
    },
    onSuccess: () => {
      setStep('code');
      setError(null);
      SMLAnalytics.track('signin_code_sent', { resend: false, result: 'ok' }).catch(console.error);
    },
    onError: (err) => {
      setError(err);
      onError?.(err);
      SMLAnalytics.track('signin_code_sent', { resend: false, result: codeSendResult(err) }).catch(console.error);
    },
  });

  // Verify code mutation
  const verifyCodeMutation = useTRPCMutation({
    mutationFn: async (code: string) => {
      const referralCode = getReferralCode() ?? undefined;
      const result = await trpcClient.auth.verifyCode.mutate({
        email,
        code,
        referralCode,
        eventId,
        ...metaSignals(),
        // Where the visitor came from. music24-api keeps it on a new user only.
        attribution: getSignupAttribution(),
      });
      if (result.isNewUser) {
        clearReferralCode();
        // Fire client pixel; server CAPI fires with the same eventId so Meta dedupes.
        fbqTrack('CompleteRegistration', {}, eventId);
      }
      return result;
    },
    onSuccess: (data) => {
      setError(null);
      SMLAnalytics.track('signin_code_result', { result: 'ok' }).catch(console.error);
      trackSmlAuthFinished('email', data.isNewUser);
      onSuccess?.(data.token, data.isNewUser);
    },
    onError: (err) => {
      setError(err);
      onError?.(err);
      SMLAnalytics.track('signin_code_result', { result: codeCheckResult(err) }).catch(console.error);
    },
  });

  // Social sign-in mutation
  const socialSignInMutation = useTRPCMutation({
    mutationFn: async (params: SocialSignInInput) => {
      const referralCode = getReferralCode() ?? undefined;
      const result = await trpcClient.auth.socialSignIn.mutate({
        ...params,
        referralCode,
        eventId,
        ...metaSignals(),
        attribution: getSignupAttribution(),
      });
      if (result.isNewUser) {
        clearReferralCode();
        fbqTrack('CompleteRegistration', {}, eventId);
      }
      return result;
    },
    onSuccess: (data, params) => {
      setError(null);
      trackSmlAuthFinished(params.provider, data.isNewUser);
      onSuccess?.(data.token, data.isNewUser);
    },
    onError: (err, params) => {
      setError(err);
      onError?.(err);
      const reason = codeCheckResult(err) === 'rateLimited' ? 'rateLimited' : 'refused';
      SMLAnalytics.track('signin_failed', { method: params.provider, reason }).catch(console.error);
    },
  });

  // Resend code mutation
  const resendCodeMutation = useTRPCMutation({
    mutationFn: async () => {
      const turnstileToken = await getTurnstileTokenRef.current?.();
      return trpcClient.auth.resendCode.mutate({ email, turnstileToken });
    },
    onSuccess: () => {
      setError(null);
      SMLAnalytics.track('signin_code_sent', { resend: true, result: 'ok' }).catch(console.error);
    },
    onError: (err) => {
      setError(err);
      onError?.(err);
      SMLAnalytics.track('signin_code_sent', { resend: true, result: codeSendResult(err) }).catch(console.error);
    },
  });

  // Request code action
  const requestCode = useCallback(
    async (emailValue: string) => {
      setEmail(emailValue);
      SMLAnalytics.trackSignInMethodChosen('email', authMode()).catch(console.error);
      await requestCodeMutation.mutateAsync(emailValue);
    },
    [requestCodeMutation]
  );

  // Verify code action
  const verifyCode = useCallback(
    async (code: string) => {
      await verifyCodeMutation.mutateAsync(code);
    },
    [verifyCodeMutation]
  );

  // Resend code action
  const resendCode = useCallback(async () => {
    await resendCodeMutation.mutateAsync();
  }, [resendCodeMutation]);

  // Social sign-in action
  const socialSignIn = useCallback(
    async (params: SocialSignInInput) => {
      SMLAnalytics.trackSignInMethodChosen(params.provider, authMode()).catch(console.error);
      await socialSignInMutation.mutateAsync(params);
    },
    [socialSignInMutation]
  );

  // Go back to email step
  const goBackToEmail = useCallback(() => {
    setStep('email');
    setError(null);
  }, []);

  const showCodeStep = useCallback((emailValue: string) => {
    setEmail(emailValue);
    setStep('code');
    setError(null);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    step,
    email,
    isRequestingCode: requestCodeMutation.isPending,
    isVerifyingCode: verifyCodeMutation.isPending,
    isResendingCode: resendCodeMutation.isPending,
    isSocialSigningIn: socialSignInMutation.isPending,
    error,
    requestCode,
    verifyCode,
    resendCode,
    socialSignIn,
    goBackToEmail,
    showCodeStep,
    clearError,
  };
}
